import React, { useEffect, useMemo, useState } from 'react';
import { WebSearchModal as DSWebSearchModal } from './DSWebSearchModal';
import type { SearchResult } from './DSWebSearchModal';
export interface WebSearchResult {
  id: string;
  title: string;
  url: string;
  domain: string;
  snippet: string;
  type: 'article' | 'video' | 'pdf' | 'academic';
}
interface WebSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQuery: string;
  onImport: (sources: WebSearchResult[]) => void;
  importedSources?: WebSearchResult[];
  onRemoveImported?: (id: string) => void;
}
const generateMockResults = (query: string): WebSearchResult[] => {
  const q = query.trim();
  return [{
    id: 'r1',
    title: `Complete Guide to ${q}`,
    url: `https://www.khanacademy.org/${q.toLowerCase().replace(/\s+/g, '-')}`,
    domain: 'khanacademy.org',
    snippet: `A comprehensive overview of ${q} covering key concepts, examples, and practice exercises for students at every level.`,
    type: 'article'
  }, {
    id: 'r2',
    title: `${q} Explained in 10 Minutes`,
    url: `https://www.youtube.com/watch?v=abc123`,
    domain: 'youtube.com',
    snippet: `Watch this detailed video explanation of ${q} with visual demonstrations and step-by-step walkthroughs.`,
    type: 'video'
  }, {
    id: 'r3',
    title: `${q}: A Systematic Review`,
    url: `https://scholar.google.com/article/${q.toLowerCase().replace(/\s+/g, '-')}`,
    domain: 'scholar.google.com',
    snippet: `This peer-reviewed paper examines ${q} through multiple theoretical frameworks and empirical studies from leading researchers.`,
    type: 'academic'
  }, {
    id: 'r4',
    title: `${q} Study Notes (PDF)`,
    url: `https://www.studylib.net/${q.toLowerCase().replace(/\s+/g, '-')}.pdf`,
    domain: 'studylib.net',
    snippet: `Download comprehensive study notes on ${q} including diagrams, formulas, and worked examples for exam preparation.`,
    type: 'pdf'
  }, {
    id: 'r5',
    title: `${q} — Britannica Encyclopedia`,
    url: `https://www.britannica.com/${q.toLowerCase().replace(/\s+/g, '-')}`,
    domain: 'britannica.com',
    snippet: `Clear and concise explanation of ${q} suitable for all learning levels with historical context and modern relevance.`,
    type: 'article'
  }, {
    id: 'r6',
    title: `Interactive ${q} Tutorial`,
    url: `https://www.coursera.org/learn/${q.toLowerCase().replace(/\s+/g, '-')}`,
    domain: 'coursera.org',
    snippet: `Hands-on interactive tutorial covering ${q} with quizzes, assignments, and real-world applications from top universities.`,
    type: 'article'
  }, {
    id: 'r7',
    title: `Advanced ${q} — Research Paper`,
    url: `https://arxiv.org/abs/2024.${q.toLowerCase().replace(/\s+/g, '')}`,
    domain: 'arxiv.org',
    snippet: `A cutting-edge research paper exploring advanced topics in ${q} with novel methodologies and experimental results.`,
    type: 'academic'
  }, {
    id: 'r8',
    title: `${q} Crash Course | Video Series`,
    url: `https://www.youtube.com/watch?v=xyz789`,
    domain: 'youtube.com',
    snippet: `A multi-part video series breaking down ${q} into digestible lessons with engaging visuals and clear explanations.`,
    type: 'video'
  }];
};
/** Map old type value to DS type value */
const toDSType = (t: WebSearchResult['type']): SearchResult['type'] | undefined => {
  if (t === 'academic') return 'paper';
  return t as SearchResult['type'];
};
/** Map DS type value back to old type value */
const fromDSType = (t: SearchResult['type'] | undefined): WebSearchResult['type'] => {
  if (t === 'paper') return 'academic';
  return (t || 'article') as WebSearchResult['type'];
};
export function WebSearchModal({
  isOpen,
  onClose,
  initialQuery,
  onImport,
  importedSources = [],
  onRemoveImported
}: WebSearchModalProps) {
  const [fullResults, setFullResults] = useState<WebSearchResult[]>([]);
  const [dsResults, setDsResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const importedIds = useMemo(() => new Set(importedSources.map(s => s.id)), [importedSources]);
  // Auto-search when modal opens with an initial query
  useEffect(() => {
    if (isOpen && initialQuery.trim()) {
      handleSearch(initialQuery);
    } else if (isOpen) {
      setFullResults([]);
      setDsResults([]);
    }
  }, [isOpen, initialQuery]);
  const handleSearch = (query: string) => {
    if (!query.trim()) return;
    setIsSearching(true);
    setFullResults([]);
    setDsResults([]);
    setTimeout(() => {
      const mockResults = generateMockResults(query);
      setFullResults(mockResults);
      setDsResults(mockResults.map(r => ({
        id: r.id,
        title: r.title,
        url: r.url,
        domain: r.domain,
        type: toDSType(r.type)
      })));
      setIsSearching(false);
    }, 1400);
  };
  const handleImport = (selectedResults: SearchResult[]) => {
    // Map DS SearchResult back to full WebSearchResult using stored data
    const mapped: WebSearchResult[] = selectedResults.map(sr => {
      const full = fullResults.find(r => r.id === sr.id);
      if (full) return full;
      return {
        id: sr.id,
        title: sr.title,
        url: sr.url,
        domain: sr.domain || '',
        snippet: '',
        type: fromDSType(sr.type)
      };
    });
    onImport(mapped);
    onClose();
  };
  return <DSWebSearchModal isOpen={isOpen} onClose={onClose} onSearch={handleSearch} onImport={handleImport} results={dsResults} isSearching={isSearching} importedIds={importedIds} data-id="web-search-modal" />;
}